import CustomLoadingButton from 'Components/CustomLoadingButton'
import StyledForm from 'Components/StyledForm'
import ActivePeriodWholeDayInput from 'Services/ProductManagement/Features/Product/Components/ActivePeriodWholeDayInput'
import {
  DaysOfTheWeek,
  type Product,
  ProductTabType,
  updateSingleProduct,
  type Validity
} from 'Services/ProductManagement/Features/Product/productSlice'
import { PRODUCT_DETAILS_DRAWER_TAB_PARAM_KEY } from 'Services/ProductManagement/Features/Tariff/Pages/TariffProductListPage'
import useTypedSelector, { useAppDispatch } from 'Store'
import SnackbarUtils from 'Util/Snackbar'
import { Button, FormControlLabel, FormGroup, Switch, Typography } from '@mui/material'
import { Box } from '@mui/system'
import { unwrapResult } from '@reduxjs/toolkit'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { t } from 'ttag'

type DayType = 'EVERYDAY' | 'WEEKDAYS' | 'SATURDAY' | 'SUNDAY'

interface ProductAvailability {
  product: Product
  successBaseUrl: string
  handleClose: () => void
  isEdited: boolean
  setIsEdited: (isEdited: boolean) => void
}

export default function ProductAvailability({
  product,
  successBaseUrl,
  handleClose,
  isEdited,
  setIsEdited
}: Readonly<ProductAvailability>) {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { submitLoading } = useTypedSelector(state => state.product)

  const [initialActivePeriods, setInitialActivePeriods] = useState<Validity[]>(
    product.activation_window.period ?? []
  )
  const [activePeriods, setActivePeriods] = useState<Validity[]>(
    product.activation_window.period ?? []
  )

  const everyDayActivePeriod = activePeriods.find(period => period.days_of_week.length === 7)
  const weekDayActivePeriod = activePeriods.find(
    period =>
      period.days_of_week.length === 5 &&
      period.days_of_week.every(day =>
        [
          DaysOfTheWeek.MONDAY,
          DaysOfTheWeek.TUESDAY,
          DaysOfTheWeek.WEDNESDAY,
          DaysOfTheWeek.THURSDAY,
          DaysOfTheWeek.FRIDAY
        ].includes(day)
      )
  )
  const saturdayActivePeriod = activePeriods.find(
    period =>
      period.days_of_week.length === 1 && period.days_of_week.includes(DaysOfTheWeek.SATURDAY)
  )
  const sundayActivePeriod = activePeriods.find(
    period => period.days_of_week.length === 1 && period.days_of_week.includes(DaysOfTheWeek.SUNDAY)
  )

  const [noRestrictionsChecked, setNoRestrictionsChecked] = useState(
    initialActivePeriods.length === 0
  )
  const [everyDayChecked, setEveryDayChecked] = useState(everyDayActivePeriod !== undefined)
  const [weekDaysChecked, setWeekDaysChecked] = useState(weekDayActivePeriod !== undefined)
  const [saturdayChecked, setSaturdayChecked] = useState(saturdayActivePeriod !== undefined)
  const [sundayChecked, setSundayChecked] = useState(sundayActivePeriod !== undefined)

  const [everyDayWholeDayChecked, setEveryDayWholeDayChecked] = useState(
    everyDayActivePeriod
      ? everyDayActivePeriod.time_of_day_from === null &&
          everyDayActivePeriod.time_of_day_until === null
      : true
  )
  const [weekDayWholeDayChecked, setWeekDayWholeDayChecked] = useState(
    weekDayActivePeriod
      ? weekDayActivePeriod.time_of_day_from === null &&
          weekDayActivePeriod.time_of_day_until === null
      : true
  )
  const [saturdayWholeDayChecked, setSaturdayWholeDayChecked] = useState(
    saturdayActivePeriod
      ? saturdayActivePeriod.time_of_day_from === null &&
          saturdayActivePeriod.time_of_day_until === null
      : true
  )
  const [sundayWholeDayChecked, setSundayWholeDayChecked] = useState(
    sundayActivePeriod
      ? sundayActivePeriod.time_of_day_from === null &&
          sundayActivePeriod.time_of_day_until === null
      : true
  )

  /**
   * Synchronizes the state of day selections based on the current checks.
   */
  const syncDaySelections = () => {
    if (weekDaysChecked && saturdayChecked && sundayChecked) {
      setEveryDayChecked(true)
      setActivePeriods([
        {
          days_of_week: [
            DaysOfTheWeek.MONDAY,
            DaysOfTheWeek.TUESDAY,
            DaysOfTheWeek.WEDNESDAY,
            DaysOfTheWeek.THURSDAY,
            DaysOfTheWeek.FRIDAY,
            DaysOfTheWeek.SATURDAY,
            DaysOfTheWeek.SUNDAY
          ],
          exceptions: [],
          time_of_day_from: null,
          time_of_day_until: null,
          title: '',
          valid_from: '',
          valid_now: true,
          valid_until: '',
          zone_id: null
        }
      ])
      setWeekDaysChecked(false)
      setSaturdayChecked(false)
      setSundayChecked(false)
    }

    if (everyDayChecked) {
      setWeekDaysChecked(false)
      setSaturdayChecked(false)
      setSundayChecked(false)
    }

    if (!everyDayChecked && !weekDaysChecked && !saturdayChecked && !sundayChecked) {
      setNoRestrictionsChecked(true)
      setActivePeriods([])
    }
  }

  useEffect(() => {
    syncDaySelections()
  }, [everyDayChecked, weekDaysChecked, saturdayChecked, sundayChecked])

  const handleNoRestrictionsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target
    setNoRestrictionsChecked(checked)
    setActivePeriods([])
    setEveryDayChecked(false)
    setWeekDaysChecked(false)
    setSaturdayChecked(false)
    setSundayChecked(false)
    setIsEdited(true)
  }

  // Check if any of the specified days are not included in the period
  const checkIfMissingAnyDay = (daysToCheck: string[], periodDays: string[]): boolean => {
    return daysToCheck.some(day => !periodDays.includes(day))
  }

  /**
   * Filters active periods by the specified days of the week and expected length.
   *
   * Removes any period that either:
   *   - Does not include all of the specified days (i.e., is missing any day in `daysOfWeek`)
   *   - Has a number of days that does not match `exactLength`
   *
   * This ensures that only periods which exactly match the specified days and length remain.
   */
  const filterActivePeriodsByDays = (daysOfWeek: string[], exactLength: number) => {
    setActivePeriods(prevState =>
      prevState.filter(period => {
        // Check if the period doesn't contain all the specified days
        // OR if the length doesn't match the expected length
        const hasDifferentLength = period.days_of_week.length !== exactLength

        const missingAnyDay = checkIfMissingAnyDay(daysOfWeek, period.days_of_week)

        return missingAnyDay || hasDifferentLength
      })
    )
  }

  const everyDayWholeDayCheckedChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    activePeriod: Validity
  ) => {
    const { checked } = event.target
    setEveryDayWholeDayChecked(checked)
    if (checked) {
      setActivePeriods(prevState =>
        prevState.map(period =>
          period === activePeriod
            ? { ...period, time_of_day_from: null, time_of_day_until: null }
            : period
        )
      )
    }
  }

  const weekDayWholeDayCheckedChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    activePeriod: Validity
  ) => {
    const { checked } = event.target
    setWeekDayWholeDayChecked(checked)
    if (checked) {
      setActivePeriods(prevState =>
        prevState.map(period =>
          period === activePeriod
            ? { ...period, time_of_day_from: null, time_of_day_until: null }
            : period
        )
      )
    }
  }

  const saturdayWholeDayCheckedChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    activePeriod: Validity
  ) => {
    const { checked } = event.target
    setSaturdayWholeDayChecked(checked)
    if (checked) {
      setActivePeriods(prevState =>
        prevState.map(period =>
          period === activePeriod
            ? { ...period, time_of_day_from: null, time_of_day_until: null }
            : period
        )
      )
    }
    setIsEdited(true)
  }

  const sundayWholeDayCheckedChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    activePeriod: Validity
  ) => {
    const { checked } = event.target
    setSundayWholeDayChecked(checked)
    if (checked) {
      setActivePeriods(prevState =>
        prevState.map(period =>
          period === activePeriod
            ? { ...period, time_of_day_from: null, time_of_day_until: null }
            : period
        )
      )
    }
  }

  const dayTypeConfig: Record<
    DayType,
    { days: DaysOfTheWeek[]; length: number; setChecked: (checked: boolean) => void }
  > = {
    EVERYDAY: {
      days: [
        DaysOfTheWeek.MONDAY,
        DaysOfTheWeek.TUESDAY,
        DaysOfTheWeek.WEDNESDAY,
        DaysOfTheWeek.THURSDAY,
        DaysOfTheWeek.FRIDAY,
        DaysOfTheWeek.SATURDAY,
        DaysOfTheWeek.SUNDAY
      ],
      length: 7,
      setChecked: setEveryDayChecked
    },
    WEEKDAYS: {
      days: [
        DaysOfTheWeek.MONDAY,
        DaysOfTheWeek.TUESDAY,
        DaysOfTheWeek.WEDNESDAY,
        DaysOfTheWeek.THURSDAY,
        DaysOfTheWeek.FRIDAY
      ],
      length: 5,
      setChecked: setWeekDaysChecked
    },
    SATURDAY: {
      days: [DaysOfTheWeek.SATURDAY],
      length: 1,
      setChecked: setSaturdayChecked
    },
    SUNDAY: {
      days: [DaysOfTheWeek.SUNDAY],
      length: 1,
      setChecked: setSundayChecked
    }
  }

  const handleValidityChange =
    (dayType: DayType) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const { checked } = event.target
      const config = dayTypeConfig[dayType]
      if (checked) {
        setActivePeriods([
          ...activePeriods,
          {
            days_of_week: config.days,
            exceptions: [],
            time_of_day_from: null,
            time_of_day_until: null,
            title: '',
            valid_from: '',
            valid_now: true,
            valid_until: '',
            zone_id: null
          }
        ])
      } else {
        filterActivePeriodsByDays(config.days, config.length)
      }
      config.setChecked(checked)
      setIsEdited(true)
    }

  const handleSave = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const productData = {
      ...product,
      activation_window: {
        ...product.activation_window,
        period: activePeriods
      }
    }

    dispatch(updateSingleProduct({ ...productData, id: product.id }))
      .then(unwrapResult)
      .then(() => {
        SnackbarUtils.success(t`Product successfully updated.`)
        setInitialActivePeriods([...activePeriods])
        setIsEdited(false)
        navigate(
          `${successBaseUrl}&${PRODUCT_DETAILS_DRAWER_TAB_PARAM_KEY}=${ProductTabType.AVAILABILITY}`
        )
      })
      .catch(() => {
        SnackbarUtils.error(t`There was an error updating the product.`)
      })
  }

  /**
   * Handles time picker changes and ensures time constraints are properly enforced.
   *
   * When validation is handled in CustomTimePicker, it triggers a
   * separate onChange call that lacks the context of which period is being updated, causing
   * the updates to be inconsistent. That is why we handle the time picker changes here
   * and ensure that the time constraints are respected.
   */
  const handleTimePickerChange = (value: Date | null, name: string, periodToUpdate: Validity) => {
    if (value instanceof Date) {
      // Format time to HH:MM:SS
      const hours = value.getHours().toString().padStart(2, '0')
      const minutes = value.getMinutes().toString().padStart(2, '0')
      const seconds = value.getSeconds().toString().padStart(2, '0')
      const timeValue = `${hours}:${minutes}:${seconds}`

      setActivePeriods(prevPeriods => {
        return prevPeriods.map(period => {
          if (period === periodToUpdate) {
            // First create a new period with the updated field
            const updatedPeriod = { ...period, [name]: timeValue }

            // Check if we need to synchronize time_of_day_from and time_of_day_until
            if (
              name === 'time_of_day_until' &&
              updatedPeriod.time_of_day_from &&
              timeValue < updatedPeriod.time_of_day_from
            ) {
              updatedPeriod.time_of_day_from = timeValue
            } else if (
              name === 'time_of_day_from' &&
              updatedPeriod.time_of_day_until &&
              timeValue > updatedPeriod.time_of_day_until
            ) {
              updatedPeriod.time_of_day_until = timeValue
            }

            return updatedPeriod
          }
          return period
        })
      })
    }
    setIsEdited(true)
  }

  const isSubmitLoading = submitLoading === 'pending'

  return (
    <StyledForm onSubmit={handleSave} isDisabled={isSubmitLoading}>
      <Box p={2}>
        <Typography variant="subtitle2" fontWeight="bold">
          {t`Product's daily availability`}
        </Typography>
        <FormGroup>
          <Typography variant="body2" mb={1}>
            {t`The ticket is available by default on all days and times. 
            You can limit the availability of the ticket by selecting specific availability 
            dates from the options below.`}
          </Typography>
          <Box ml={2} display="flex" flexDirection="column">
            <FormControlLabel
              control={
                <Switch onChange={handleNoRestrictionsChange} checked={noRestrictionsChecked} />
              }
              label={t`No restrictions (Default)`}
            />
            <FormControlLabel
              control={
                <Switch
                  onChange={handleValidityChange('EVERYDAY')}
                  disabled={
                    weekDaysChecked || saturdayChecked || sundayChecked || noRestrictionsChecked
                  }
                  checked={everyDayChecked}
                />
              }
              label={t`Everyday`}
            />
            <Box ml={1}>
              {everyDayChecked && everyDayActivePeriod && (
                <ActivePeriodWholeDayInput
                  isChecked={everyDayWholeDayChecked}
                  handleCheckedChange={e => everyDayWholeDayCheckedChange(e, everyDayActivePeriod)}
                  activePeriod={everyDayActivePeriod}
                  handleTimePickerChange={handleTimePickerChange}
                />
              )}
            </Box>
            <FormControlLabel
              control={
                <Switch
                  onChange={handleValidityChange('WEEKDAYS')}
                  disabled={everyDayChecked || noRestrictionsChecked}
                  checked={weekDaysChecked}
                />
              }
              label={t`Weekdays`}
            />
            <Box ml={1}>
              {weekDaysChecked && weekDayActivePeriod && (
                <ActivePeriodWholeDayInput
                  isChecked={weekDayWholeDayChecked}
                  handleCheckedChange={e => weekDayWholeDayCheckedChange(e, weekDayActivePeriod)}
                  activePeriod={weekDayActivePeriod}
                  handleTimePickerChange={handleTimePickerChange}
                />
              )}
            </Box>
            <FormControlLabel
              control={
                <Switch
                  onChange={handleValidityChange('SATURDAY')}
                  disabled={everyDayChecked || noRestrictionsChecked}
                  checked={saturdayChecked}
                />
              }
              label={t`Saturday`}
            />
            <Box ml={1}>
              {saturdayChecked && saturdayActivePeriod && (
                <ActivePeriodWholeDayInput
                  isChecked={saturdayWholeDayChecked}
                  handleCheckedChange={e => saturdayWholeDayCheckedChange(e, saturdayActivePeriod)}
                  activePeriod={saturdayActivePeriod}
                  handleTimePickerChange={handleTimePickerChange}
                />
              )}
            </Box>
            <FormControlLabel
              control={
                <Switch
                  onChange={handleValidityChange('SUNDAY')}
                  disabled={everyDayChecked || noRestrictionsChecked}
                  checked={sundayChecked}
                />
              }
              label={t`Sunday`}
            />
            <Box ml={1}>
              {sundayChecked && sundayActivePeriod && (
                <ActivePeriodWholeDayInput
                  isChecked={sundayWholeDayChecked}
                  handleCheckedChange={e => sundayWholeDayCheckedChange(e, sundayActivePeriod)}
                  activePeriod={sundayActivePeriod}
                  handleTimePickerChange={handleTimePickerChange}
                />
              )}
            </Box>
          </Box>
        </FormGroup>
        <Box display="flex" gap={2} mt={2}>
          <Button onClick={handleClose} size="medium" variant="outlined" color="inherit">
            {t`Cancel`}
          </Button>
          <CustomLoadingButton
            size="medium"
            type="submit"
            disabled={!isEdited}
            isLoading={isSubmitLoading}
            iconPosition="end"
          >
            {t`Save changes`}
          </CustomLoadingButton>
        </Box>
      </Box>
    </StyledForm>
  )
}
